import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import type { TaxStatementBatch } from '../../data/types';
import AmmaStatementView from '../../documents/AmmaStatementView';
import { DEMO_TODAY } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';

export default function StatementPreviewDialog({
  batch,
  onClose,
}: {
  batch: TaxStatementBatch | null;
  onClose: () => void;
}) {
  const scope = useTenantScope();
  const { state } = useRegistry();
  const holderIds = useMemo(
    () =>
      Array.from(
        new Set(
          state.ledger
            .filter((entry) => entry.fundId === batch?.fundId)
            .map((entry) => entry.holderId),
        ),
      ),
    [state.ledger, batch],
  );
  const [chosen, setChosen] = useState('');
  const holderId = chosen && holderIds.includes(chosen) ? chosen : (holderIds[0] ?? '');
  return (
    <Dialog open={Boolean(batch)} onClose={onClose} maxWidth="md" fullWidth scroll="body">
      <DialogTitle sx={{ fontWeight: 700, pr: 7 }}>
        {batch
          ? `${scope.fundById.get(batch.fundId)?.code} ${batch.financialYear} statement preview`
          : ''}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: '#F1F5F9', pt: '16px !important' }}>
        {batch && (
          <>
            <TextField
              select
              fullWidth
              size="small"
              label="Holder"
              value={holderId}
              onChange={(event) => setChosen(event.target.value)}
              sx={{ mb: 2, bgcolor: '#fff' }}
            >
              {holderIds.map((id) => (
                <MenuItem key={id} value={id}>
                  {scope.holderById.get(id)?.name}
                </MenuItem>
              ))}
            </TextField>
            {batch.financialYear === 'FY2027' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                FY2027 is still running, so this preview covers distributions paid to date. Final
                components come from the fund accountant after 30 June 2027.
              </Alert>
            )}
            {holderId && (
              <AmmaStatementView
                state={state}
                holderId={holderId}
                financialYear={batch.financialYear}
                fundId={batch.fundId}
                issued={batch.publishedAt ?? DEMO_TODAY}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
